import * as React from "react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils";

type MessageContext =
  | "general"
  | "extraction"
  | "synthesis"
  | "breakdown"
  | "activity"
  | "prompt";

const MESSAGE_KEYS: Record<MessageContext, string[]> = {
  general: [
    "generating_loader.general_1",
    "generating_loader.general_2",
    "generating_loader.general_3",
    "generating_loader.general_4",
    "generating_loader.general_5",
  ],
  prompt: [
    "generating_loader.prompt_1",
    "generating_loader.prompt_2",
    "generating_loader.prompt_3",
    "generating_loader.prompt_4",
    "generating_loader.prompt_5",
  ],
  extraction: [
    "generating_loader.extraction_1",
    "generating_loader.extraction_2",
    "generating_loader.extraction_3",
    "generating_loader.extraction_4",
    "generating_loader.extraction_5",
  ],
  synthesis: [
    "generating_loader.synthesis_1",
    "generating_loader.synthesis_2",
    "generating_loader.synthesis_3",
    "generating_loader.synthesis_4",
    "generating_loader.synthesis_5",
  ],
  breakdown: [
    "generating_loader.breakdown_1",
    "generating_loader.breakdown_2",
    "generating_loader.breakdown_3",
    "generating_loader.breakdown_4",
    "generating_loader.breakdown_5",
  ],
  activity: [
    "generating_loader.activity_1",
    "generating_loader.activity_2",
    "generating_loader.activity_3",
    "generating_loader.activity_4",
    "generating_loader.activity_5",
  ],
};

const FALLBACK_MESSAGES: Record<MessageContext, string[]> = {
  general: [
    "Thinking deeply...",
    "Crafting a response...",
    "Working some magic...",
    "Connecting the dots...",
    "Almost there...",
  ],
  prompt: [
    "Thinking deeply...",
    "Exploring ideas...",
    "Crafting a response...",
    "Formulating thoughts...",
    "Almost there...",
  ],
  extraction: [
    "Reading between the lines...",
    "Spotting insights...",
    "Identifying action items...",
    "Analyzing patterns...",
    "Making sense of it all...",
  ],
  synthesis: [
    "Connecting the dots...",
    "Finding patterns...",
    "Discovering themes...",
    "Weaving insights together...",
    "Synthesizing your wisdom...",
  ],
  breakdown: [
    "Breaking it down...",
    "Finding the right steps...",
    "Organizing tasks...",
    "Planning the path forward...",
    "Structuring your approach...",
  ],
  activity: [
    "Building your activity...",
    "Adding structure...",
    "Making it interactive...",
    "Designing the flow...",
    "Crafting the experience...",
  ],
};

export interface GeneratingLoaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  context?: MessageContext;
  messages?: string[];
  subtitle?: string;
  interval?: number;
}

const GeneratingLoader = React.forwardRef<HTMLDivElement, GeneratingLoaderProps>(
  (
    {
      className,
      context = "general",
      messages: customMessages,
      subtitle,
      interval = 2500,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [isVisible, setIsVisible] = React.useState(true);

    const resolvedMessages = React.useMemo(() => {
      if (customMessages && customMessages.length > 0) return customMessages;
      const keys = MESSAGE_KEYS[context];
      const fallbacks = FALLBACK_MESSAGES[context];
      return keys.map((key, i) => {
        const translated = t(key);
        return translated === key ? fallbacks[i] : translated;
      });
    }, [customMessages, context, t]);

    React.useEffect(() => {
      if (resolvedMessages.length <= 1) return;

      const timer = setInterval(() => {
        setIsVisible(false);
        setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % resolvedMessages.length);
          setIsVisible(true);
        }, 300);
      }, interval);

      return () => clearInterval(timer);
    }, [resolvedMessages.length, interval]);

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center gap-5",
          className
        )}
        {...props}
      >
        {/* Animated icon box */}
        <div className="relative flex items-center justify-center">
          <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
            <Sparkles className="h-9 w-9 text-primary-foreground animate-pulse" />
          </div>
          {/* Ping accent dots */}
          <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary/40 animate-ping" />
          <div
            className="absolute -bottom-1 -left-1 h-2.5 w-2.5 rounded-full bg-primary/30 animate-ping"
            style={{ animationDelay: "0.5s" }}
          />
        </div>

        {/* Rotating message */}
        <div className="min-h-[3rem] flex flex-col items-center justify-center gap-1">
          <p
            className={cn(
              "text-sm font-medium text-foreground transition-all duration-300",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-1"
            )}
          >
            {resolvedMessages[currentIndex]}
          </p>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
      </div>
    );
  }
);
GeneratingLoader.displayName = "GeneratingLoader";

export { GeneratingLoader };
export type { MessageContext };
